import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  inject,
  OnDestroy,
  OnInit,
  Signal,
  WritableSignal
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  OrAvatarComponent,
  OrIconComponent,
  PageActionsComponent,
  PageLayoutComponent,
  SelectableSingleRadioModalComponent,
  SingleRadioItemModel,
  TitileNavbarComponent
} from '@ui-components';
import { Observable, take } from 'rxjs';
import { NgClass } from '@angular/common';
import {
  RemoveAccountCommandHandler,
  SetReferrerCommandHandler,
  UpdateUserCommandHandler
} from '../../application/handlers';
import { IonicModule } from '@ionic/angular';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  ActionModalComponent,
  availableLevels,
  gameLevelMapper,
  MODAL_TOKEN,
  ModalProvider,
  PLATFORM_TOKEN,
  PlatformProvider,
  preferredPositionMapper,
  UserModel
} from '@core';
import { AppPreferredPlayerPosition, GameLevelEnum } from '@core-http';
import { Base64imageModel, EditProfileViewService } from './view-service/edit-profile.view-service';
import {
  RemoveAccountModalComponent,
  RemoveAccountModalResult
} from './remove-account-modal/remove-account-modal.component';

@Component({
  selector: 'lib-edit-profile',
  templateUrl: './edit-profile.component.html',
  styleUrls: ['./edit-profile.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    NgClass,
    TitileNavbarComponent,
    IonicModule,
    ReactiveFormsModule,
    OrAvatarComponent,
    PageLayoutComponent,
    PageActionsComponent,
    OrIconComponent
  ],
  providers: [UpdateUserCommandHandler, SetReferrerCommandHandler, RemoveAccountCommandHandler, EditProfileViewService]
})
export class EditProfileComponent implements OnInit, OnDestroy {
  private readonly platformProvider: PlatformProvider = inject(PLATFORM_TOKEN);
  private readonly viewService: EditProfileViewService = inject(EditProfileViewService);
  private readonly modalProvider: ModalProvider = inject(MODAL_TOKEN);
  private readonly cdr: ChangeDetectorRef = inject(ChangeDetectorRef);

  readonly isiOS: boolean = this.platformProvider.isiOS;
  readonly preferredLevel: GameLevelEnum[] = availableLevels.filter(
    (level: GameLevelEnum) => level !== GameLevelEnum.MIXED
  );
  readonly gameLevelMapper: Record<GameLevelEnum, string> = gameLevelMapper;
  readonly preferredPosition: AppPreferredPlayerPosition[] = Object.values(AppPreferredPlayerPosition);
  readonly preferredPositionMapper: Record<AppPreferredPlayerPosition, string> = preferredPositionMapper;
  readonly notSpecifiedLabel: string = 'Nie podawaj';

  readonly profileForm: FormGroup = new FormGroup({
    name: new FormControl('', [Validators.required, Validators.maxLength(15)]),
    lastName: new FormControl('', [Validators.maxLength(15)]),
    phone: new FormControl('', [Validators.minLength(9), Validators.maxLength(9), Validators.pattern('^[0-9]*$')]),
    avatar: new FormControl(),
    preferredLevel: new FormControl(),
    preferredPosition: new FormControl(),
    referrerToken: new FormControl()
  });

  readonly base64image: WritableSignal<Base64imageModel> = this.viewService.base64image;
  readonly userData: Signal<UserModel | undefined> = toSignal(this.viewService.userData$);

  private readonly nameValue: Signal<string | undefined> = toSignal(
    this.profileForm.get('name')!.valueChanges as Observable<string>
  );

  private readonly lastNameValue: Signal<string | undefined> = toSignal(
    this.profileForm.get('lastName')!.valueChanges as Observable<string>
  );

  readonly isHidden: Signal<boolean> = computed(() => {
    const user: UserModel | undefined = this.userData();
    if (!user) return false;
    const isAppleEmail: boolean = user.email.includes('apple');
    return this.isiOS && isAppleEmail && !user.name;
  });

  readonly isNameOverAvailableLength: Signal<boolean> = computed(() => {
    const name: string | undefined = this.nameValue();
    return !!name && name.length > 15;
  });

  readonly isLastNameOverAvailableLength: Signal<boolean> = computed(() => {
    const lastName: string | undefined = this.lastNameValue();
    return !!lastName && lastName.length > 15;
  });

  ngOnInit(): void {
    this.initializeForm();
  }

  ngOnDestroy(): void {
    this.viewService.cleanupSessionStorage();
  }

  get preferredPositionLabel(): string {
    const value: AppPreferredPlayerPosition | null = this.profileForm.get('preferredPosition')?.value;
    return value ? this.preferredPositionMapper[value] : this.notSpecifiedLabel;
  }

  get preferredLevelLabel(): string {
    const value: GameLevelEnum | null = this.profileForm.get('preferredLevel')?.value;
    return value ? this.gameLevelMapper[value] : this.notSpecifiedLabel;
  }

  saveData(): void {
    this.viewService.saveData(this.profileForm);
  }

  cancel(): void {
    if (!this.viewService.hasUnsavedChanges(this.profileForm)) {
      this.viewService.goToSettings(true);
      return;
    }

    this.modalProvider
      .showModal$({
        component: ActionModalComponent,
        componentProps: {
          header: 'Niezapisane zmiany',
          message: 'Masz niezapisane zmiany. Czy na pewno chcesz opuścić edycję profilu?',
          btnOk: 'Opuść',
          btnCancel: 'Zostań',
          action: () => this.viewService.goToSettings(true)
        },
        cssClass: 'present-modal'
      })
      .pipe(take(1))
      .subscribe();
  }

  openPositionModal(): void {
    const current: AppPreferredPlayerPosition | '' = this.profileForm.get('preferredPosition')?.value ?? '';

    const payload: SingleRadioItemModel<string>[] = [
      { name: this.notSpecifiedLabel, value: '' },
      ...this.preferredPosition.map((position: AppPreferredPlayerPosition) => ({
        name: this.preferredPositionMapper[position],
        value: position as string
      }))
    ];

    this.modalProvider
      .showModal$<string>({
        component: SelectableSingleRadioModalComponent,
        cssClass: 'present-modal',
        backdropDismiss: false,
        componentProps: {
          buttonText: 'Wybierz',
          header: 'Preferowana pozycja',
          providedData: payload,
          selectedData: current
        }
      })
      .pipe(take(1))
      .subscribe((result: string) => {
        this.profileForm.get('preferredPosition')?.setValue(result ? result : null);
        this.cdr.markForCheck();
      });
  }

  openLevelModal(): void {
    const current: GameLevelEnum | '' = this.profileForm.get('preferredLevel')?.value ?? '';

    const payload: SingleRadioItemModel<string>[] = [
      { name: this.notSpecifiedLabel, value: '' },
      ...this.preferredLevel.map((level: GameLevelEnum) => ({
        name: this.gameLevelMapper[level],
        value: level as string
      }))
    ];

    this.modalProvider
      .showModal$<string>({
        component: SelectableSingleRadioModalComponent,
        cssClass: 'present-modal',
        backdropDismiss: false,
        componentProps: {
          buttonText: 'Wybierz',
          header: 'Twój poziom',
          providedData: payload,
          selectedData: current
        }
      })
      .pipe(take(1))
      .subscribe((result: string) => {
        this.profileForm.get('preferredLevel')?.setValue(result ? result : null);
        this.cdr.markForCheck();
      });
  }

  isFieldInvalid(fieldName: string): boolean {
    const control: AbstractControl<unknown, unknown, unknown> | null = this.profileForm.get(fieldName);
    return !!control?.touched && !!control?.invalid;
  }

  changeAvatar(): void {
    this.viewService.changeAvatar(this.profileForm);
  }

  openRemoveAccountModal(): void {
    this.modalProvider
      .showModal$<RemoveAccountModalResult>({
        component: RemoveAccountModalComponent,
        cssClass: 'present-modal'
      })
      .pipe(take(1))
      .subscribe((result?: RemoveAccountModalResult) => {
        if (result?.confirmed) this.viewService.deleteAccount(result.password);
      });
  }

  private initializeForm(): void {
    this.viewService.initializeForm(this.profileForm);
  }
}
